import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils.js';

/*
 * shadcn's table, re-authored to the console's rule-lined treatment: a strong
 * rule under the head, a hairline between rows, no zebra striping, and numbers
 * inheriting the tabular figures set on <html>.
 *
 * The wrapper uses the shared `.scroll-x` class so wide tables scroll inside
 * themselves and the page body never scrolls sideways.
 */
export function Table({ className, ...props }: ComponentProps<'table'>) {
  return (
    <div data-slot="table-container" className="scroll-x">
      <table
        data-slot="table"
        className={cn('w-full border-collapse text-left', className)}
        {...props}
      />
    </div>
  );
}

export function TableHeader({ className, ...props }: ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn('[&_tr]:border-b [&_tr]:border-rule-strong [&_tr]:hover:bg-transparent', className)}
      {...props}
    />
  );
}

export function TableBody({ className, ...props }: ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" className={cn(className)} {...props} />;
}

export function TableFooter({ className, ...props }: ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t border-rule-strong font-medium', className)}
      {...props}
    />
  );
}

export function TableRow({ className, ...props }: ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'border-b border-rule transition-colors duration-(--duration-fast)',
        'hover:bg-surface-hover data-[state=selected]:bg-accent-quiet',
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'px-(--space-3) py-(--space-2) align-middle font-medium whitespace-nowrap',
        'text-[length:var(--text-micro)] tracking-wide uppercase text-ink-subtle',
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn('px-(--space-3) py-(--space-2) align-middle', className)}
      {...props}
    />
  );
}

export function TableCaption({ className, ...props }: ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-(--space-2) text-[length:var(--text-small)] text-ink-muted', className)}
      {...props}
    />
  );
}
